import { useEffect, type ReactNode } from 'react';
import { mapErrorCode } from '../api/errorMapper.js';
import { setActiveTheme } from './activeTheme.js';
import { resolveThemeForScreen } from './themeRegisters.js';

/**
 * 07-02 §Screen State Register. `idle` is retained as the legacy spelling of
 * `success`; both render the resolved payload.
 */
export type ScreenState =
  'idle' | 'success' | 'loading' | 'empty' | 'error' | 'partial' | 'offline';

/**
 * 07-02: a tick means the screen operates from a local queue, a half-filled
 * circle means it renders the last cached payload read-only with a staleness
 * marker, and a cross means it blocks with the offline banner.
 */
export type OfflineMode = 'queue' | 'degraded' | 'blocked';

export interface ScreenFrameProps {
  screenId: string;
  title: string;
  state?: ScreenState;
  errorCode?: string;
  errorMessage?: string;
  emptyMessage?: string;
  /** Names the units that failed while others resolved, for `partial`. */
  failedRegions?: string[];
  offlineMode?: OfflineMode;
  /** Staleness marker for `offline` in degraded mode. */
  cachedAtLabel?: string;
  queuedCount?: number;
  /** When false, keep `data-screen-id` but hide the visible screen-id line (auth surfaces). */
  showScreenId?: boolean;
  /**
   * When true, children stay mounted during `loading` (auth forms keep fields visible;
   * submit buttons convey busy state).
   */
  keepContentOnLoading?: boolean;
  /**
   * When true, children stay mounted during `empty` (queue toolbars/filters remain usable).
   */
  keepContentOnEmpty?: boolean;
  /**
   * When true, children stay mounted during `error` (recovery CTAs remain usable).
   */
  keepContentOnError?: boolean;
  /** When false, hide the visible page title (keep sr-only h1 for accessibility). */
  showTitle?: boolean;
  /** When `none`, omit default section padding (marketplace pages manage spacing). */
  padding?: 'default' | 'none';
  children: ReactNode;
}

const BANNER = 'flex items-center gap-inline border-l-4 px-field py-inline text-body';

export function ScreenFrame({
  screenId,
  title,
  state = 'idle',
  errorCode,
  errorMessage,
  emptyMessage = 'Nothing to show yet.',
  failedRegions,
  offlineMode = 'degraded',
  cachedAtLabel,
  queuedCount,
  showScreenId = true,
  showTitle = true,
  padding = 'default',
  keepContentOnLoading = false,
  keepContentOnEmpty = false,
  keepContentOnError = false,
  children,
}: ScreenFrameProps) {
  const screenTheme = resolveThemeForScreen(screenId);
  const resolvedError = errorMessage ?? mapErrorCode(errorCode ?? 'ERR-UNKNOWN');

  // Drive app chrome (html + ThemeProvider wrapper) from the active screen.
  // Do not reset on unmount — the next ScreenFrame sets the next theme.
  useEffect(() => {
    setActiveTheme(screenTheme);
  }, [screenTheme]);

  // 07-02 Partial: "The failed unit renders its own error in place; the page
  // does not become an error." Offline degraded renders the cached payload
  // read-only. Both keep children mounted.
  const showsContent =
    state === 'idle' ||
    state === 'success' ||
    state === 'partial' ||
    (keepContentOnLoading && state === 'loading') ||
    (keepContentOnEmpty && state === 'empty') ||
    (keepContentOnError && state === 'error') ||
    (state === 'offline' && offlineMode !== 'blocked');

  return (
    <section
      data-screen-id={screenId}
      data-screen-state={state}
      data-theme={screenTheme}
      className={padding === 'none' ? 'w-full min-w-0' : 'w-full min-w-0 p-lg'}
    >
      {showTitle ? (
        <h1 className="mb-xs max-w-full break-words">{title}</h1>
      ) : (
        <h1 className="sr-only">{title}</h1>
      )}
      {showScreenId ? <p className="m-0 text-body-sm text-muted">{screenId}</p> : null}

      {state === 'loading' && !keepContentOnLoading ? (
        // 07-02: a skeleton at the shape of the resolved content, never a
        // centred spinner on a full page.
        <div role="status" aria-live="polite" aria-busy="true" className="mt-field">
          <span className="sr-only">Loading {title}</span>
          <div aria-hidden="true" className="flex flex-col gap-xs">
            <span className="block h-4 animate-pulse rounded-control bg-sunken" />
            <span className="block h-4 animate-pulse rounded-control bg-sunken" />
            <span className="block h-4 w-3/5 animate-pulse rounded-control bg-sunken" />
          </div>
        </div>
      ) : null}

      {state === 'empty' && !keepContentOnEmpty ? (
        <p role="status" aria-live="polite" className="mt-field">
          {emptyMessage}
        </p>
      ) : null}

      {state === 'error' ? (
        <p role="alert" className="mt-field text-danger">
          {resolvedError}
        </p>
      ) : null}

      {state === 'partial' ? (
        <div
          role="status"
          aria-live="polite"
          className={`${BANNER} mt-field border-late text-caution`}
        >
          {failedRegions?.length
            ? `Some parts of this page could not be loaded: ${failedRegions.join(', ')}.`
            : 'Some parts of this page could not be loaded.'}
        </div>
      ) : null}

      {state === 'offline' ? (
        <div
          role="region"
          aria-label="Connection"
          aria-live="assertive"
          className={`${BANNER} mt-field border-blocked text-negative`}
        >
          {offlineMode === 'queue'
            ? `You are offline. Actions are queued and will sync on reconnect${
                queuedCount ? ` (${queuedCount} queued)` : ''
              }.`
            : offlineMode === 'degraded'
              ? `You are offline. Showing the last saved copy${
                  cachedAtLabel ? ` from ${cachedAtLabel}` : ''
                }; changes are unavailable.`
              : 'You are offline. This page is unavailable until the connection returns.'}
        </div>
      ) : null}

      {showsContent ? children : null}
    </section>
  );
}
